import { styled } from "goober";
import type * as z from "zod";
import type { askQuestion as askQuestionTool } from "../../api/ai/tools/askQuestion";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { Text } from "../components/Text";
import { AskQuestionPrompt } from "./AskQuestionPrompt";

type Input = z.infer<ReturnType<typeof askQuestionTool>["inputSchema"]>;
export type AskQuestionItem = Input["questions"][number];
export type Answer = { selected?: string[]; text?: string };
type Output = {
  answers?: Record<string, Answer>;
  dismissed?: boolean;
};

function formatAnswer(q: AskQuestionItem, answer: Answer): string {
  const parts = [];
  if (answer.selected && q.options) {
    parts.push(
      ...q.options
        .filter((o) => answer.selected!.includes(o.id))
        .map((o) => o.label)
    );
  }
  if (answer.text) parts.push(answer.text);
  return parts.join(", ");
}

function AskQuestionHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const output = result?.output as Output | undefined;
  const input = toolUse.input as Input | undefined;

  if (!result) {
    return (
      <ToolStatus
        icon="message-circle-question-mark"
        text="Waiting for answer…"
        pending={!interrupted}
      />
    );
  }

  if (output?.dismissed) {
    return <ToolStatus icon="circle-minus" text="Question dismissed" />;
  }

  const questions = Array.isArray(input?.questions) ? input.questions : [];
  const answers = output?.answers ?? {};
  if (questions.length === 1 && questions[0]) {
    const answer = answers[questions[0].id];
    const formatted = answer ? formatAnswer(questions[0], answer) : "";
    return (
      <ToolStatus
        icon="check-filled"
        iconColor="var(--green-600)"
        text={
          formatted ? `Question answered: ${formatted}` : "Question answered"
        }
      />
    );
  }

  return (
    <ToolStatus
      icon="check-filled"
      iconColor="var(--green-600)"
      text={`${questions.length} questions answered`}
    />
  );
}

const BodyWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 6px 8px 6px;
`;

const KVRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

function AskQuestionBody({ toolUse, result }: ToolUseProps) {
  const input = toolUse.input as Input | undefined;
  const output = result?.output as Output | undefined;
  if (!input || !output) return null;

  const questions = Array.isArray(input.questions) ? input.questions : [];
  const answers = output.answers ?? {};

  if (output.dismissed) {
    return (
      <BodyWrapper>
        {questions.map((q) => (
          <KVRow key={q.id}>
            <Text variant="text-smaller-md" color="var(--text-tertiary)">
              {q.question}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Dismissed
            </Text>
          </KVRow>
        ))}
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper>
      {questions.map((q) => {
        const answer = answers[q.id];
        const text = answer ? formatAnswer(q, answer) : "";
        return (
          <KVRow key={q.id}>
            <Text variant="text-smaller-md" color="var(--text-tertiary)">
              {q.question}
            </Text>
            {text && (
              <Text variant="text-small" color="var(--text-secondary)">
                {text}
              </Text>
            )}
          </KVRow>
        );
      })}
    </BodyWrapper>
  );
}

export const askQuestion: ChatTool = {
  name: "askQuestion",
  Header: AskQuestionHeader,
  Body: AskQuestionBody,
  alwaysLoadOutput: true,
  PendingPrompt: ({ input, hasStagedAttachments, onResult }) => {
    const questions = (input as Input | null)?.questions;
    if (!Array.isArray(questions) || questions.length === 0) return null;
    return (
      <AskQuestionPrompt
        questions={questions}
        hasStagedAttachments={hasStagedAttachments}
        onSubmit={(output) =>
          onResult(output, false, { sendStagedAttachments: true })
        }
        onDismiss={() => onResult({ dismissed: true }, false)}
        onTypeInstead={() =>
          onResult({ dismissed: true }, false, { pauseAfter: true })
        }
      />
    );
  },
};
